<template>
  <div>
    <Page404 v-if="isDocomoPlan" />
    <section v-else class="p-box">
      <ValidationObserver ref="form" v-slot="{ handleSubmit, valid }">
        <form @submit.prevent="handleSubmit($event, onSubmit)">
          <div class="p-box__body">
            <div v-if="hasGmbLocation" class="p-box__info">
              <div class="c-info">
                <div class="c-info__icon">
                  <Icon name="iconQuestion" />
                </div>
                <div class="c-info__content">
                  <p>{{ $t('pages.CompaniesLocationStore.gmbProtection.edit.info2') }}</p>
                </div>
              </div>
            </div>
            <Loading v-show="loading" />
            <template v-if="dataLoaded">
              <div class="c-grid">
                <div class="c-grid__items">
                  <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__12 c-grid__item__l__12">
                    <div class="p-formGrid p-formGrid--border">
                      <div class="p-formGrid__items">
                        <fieldset v-if="inboundOption" class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <KkToggleSwitch
                            id="enableMultiLang"
                            :label="$t('pages.CompaniesLocationStoreEdit.multiLang.enableMultiLang')"
                            :model-value="enableMultiLang"
                            @update:model-value="value => enableMultiLang = value"
                          />
                        </fieldset>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__8 p-formGrid__item__l__8">
                          <label class="c-formLabel">
                            <p>{{ formatLabelText({ label: 'models.store.name', required: true }) }}</p>
                          </label>
                          <ValidationProvider key="store_name_validation" v-slot="{ errors, value, handleChange }" v-model="store.attributes.name" :name="$t('models.store.name')" rules="required">
                            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                        <fieldset v-if="hasGmbLocation" class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ul class="protection c-checkbox">
                            <li class="c-checkbox__item">
                              <input id="name_synced" v-model="gmbLocationSynced.name_synced" type="checkbox" class="c-checkbox__input">
                              <label class="c-checkbox__label" for="name_synced">
                                {{ $t('pages.CompaniesLocationStore.gmbProtection.edit.protect') }}
                              </label>
                            </li>
                          </ul>
                        </fieldset>
                        <fieldset v-if="inboundOption && enableMultiLang" class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__8 p-formGrid__item__l__8">
                          <div class="multiLang">
                            <p class="multiLang__status">
                              <StatusBadge :status="extractStatusNumber" />
                              {{ $t(`pages.CompaniesLocationStoreEdit.multiLang.${existMultiLang ? 'setMultiLang' : 'notSetMultiLang'}`) }}
                            </p>
                            <label class="c-formLabel">
                              <p>{{ formatLabelText({ label: 'pages.CompaniesLocationStoreEdit.multiLang.label.english', required: false }) }}</p>
                            </label>
                            <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                key="en_name"
                                                v-model="store.attributes.store_multi_language.en_name"
                                                :name="$t('pages.CompaniesLocationStoreEdit.multiLang.label.english')"
                                                rules="max:140"
                            >
                              <input :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                              <ValidationError :error="errors[0]" />
                            </ValidationProvider>
                            <label class="c-formLabel">
                              <p>{{ formatLabelText({ label: 'pages.CompaniesLocationStoreEdit.multiLang.label.traditionalChinese', required: false }) }}</p>
                            </label>
                            <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                key="zh_tw_name"
                                                v-model="store.attributes.store_multi_language.zh_tw_name"
                                                :name="$t('pages.CompaniesLocationStoreEdit.multiLang.label.traditionalChinese')"
                                                rules="max:140"
                            >
                              <input :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                              <ValidationError :error="errors[0]" />
                            </ValidationProvider>
                            <label class="c-formLabel">
                              <p>{{ formatLabelText({ label: 'pages.CompaniesLocationStoreEdit.multiLang.label.korean', required: false }) }}</p>
                            </label>
                            <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                key="ko_name"
                                                v-model="store.attributes.store_multi_language.ko_name"
                                                :name="$t('pages.CompaniesLocationStoreEdit.multiLang.label.korean')"
                                                rules="max:140"
                            >
                              <input :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                              <ValidationError :error="errors[0]" />
                            </ValidationProvider>
                          </div>
                          <KkInfo>
                            {{ $t('pages.CompaniesLocationStoreEdit.multiLang.notice') }}
                          </KkInfo>
                        </fieldset>
                      </div>
                    </div>
                    <div class="p-formGrid p-formGrid--border">
                      <div class="p-formGrid__items">
                        <fieldset class="p-formGrid__item p-formGrid__item__s__6 p-formGrid__item__m__4 p-formGrid__item__l__4">
                          <label class="c-formLabel">
                            <p>{{ formatLabelText({ label: 'models.store.postalCode', required: true }) }}</p>
                          </label>
                          <ValidationProvider key="postal_code_validation" v-slot="{ errors, value, handleChange }" v-model="store.attributes.postal_code" :name="$t('models.store.postalCode')" rules="required|postal_code|length:7|numeric">
                            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.postalCode')" @input="handleChange">
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__6 p-formGrid__item__m__4 p-formGrid__item__l__4">
                          <label class="c-formLabel">
                            <p>{{ formatLabelText({ label: 'models.store.prefecture', required: true }) }}</p>
                          </label>
                          <ValidationProvider key="prefecture_validation" v-slot="{ errors, value, handleChange }" v-model="store.attributes.prefecture" :name="$t('models.store.prefecture')" rules="required">
                            <div class="c-select">
                              <select :value="value" @change="handleChange">
                                <option v-for="prefecture in prefectures" :key="Object.keys(prefecture)[0]" :value="Object.keys(prefecture)[0]">
                                  {{ Object.values(prefecture)[0] }}
                                </option>
                              </select>
                            </div>
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <label class="c-formLabel">
                            <p>{{ formatLabelText({ label: 'models.store.address', required: true }) }}</p>
                          </label>
                          <div v-for="(address_line, i) in store.attributes.address_lines" :key="`address_line_${i}`">
                            <div class="c-grid">
                              <div class="phone-form-group c-grid__items">
                                <div class="c-grid__item c-grid__item__s__6 c-grid__item__m__8 c-grid__item__l__8">
                                  <ValidationProvider :key="`address_line_${i}_validation`" v-slot="{ errors, value, handleChange }" v-model="store.attributes.address_lines[i]" :name="$t('models.store.address')" rules="required" :vid="`address_line_${i}_validation`">
                                    <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                                    <ValidationError :error="errors[0]" />
                                  </ValidationProvider>
                                </div>
                                <div v-if="i !== 0" class="phone-form-remove c-grid__item c-grid__item__s__6 c-grid__item__m__4 c-grid__item__l__4">
                                  <span class="phone-form-remove__button" @click="removeAddressLineField(i)">
                                    <img :src="iconCrossPath" alt="" class="p-stores__action__icon">
                                    {{ $t('labels.delete') }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="phone-form__add c-button c-button--icon">
                            <div class="c-button__item c-button__item--primary" @click="addAddressLineField()">
                              <img :src="ImgIconPlusRed" alt="" class="c-button__icon">
                              <span>{{ $t('labels.add', {'value': $t('models.store.address')}) }}</span>
                            </div>
                          </div>
                        </fieldset>
                        <fieldset v-if="hasGmbLocation" class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ul class="protection c-checkbox">
                            <li class="c-checkbox__item">
                              <input id="address_synced" v-model="gmbLocationSynced.address_synced" type="checkbox" class="c-checkbox__input">
                              <label class="c-checkbox__label" for="address_synced">
                                {{ $t('pages.CompaniesLocationStore.gmbProtection.edit.protect') }}
                              </label>
                            </li>
                          </ul>
                        </fieldset>
                      </div>
                    </div>
                    <div class="p-formGrid p-formGrid--border">
                      <div class="p-formGrid__items">
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <label class="c-formLabel">
                            <p>{{ formatLabelText({ label: 'models.store.primary_phone', required: false }) }}</p>
                          </label>
                          <div class="c-grid">
                            <div class="phone-form-group c-grid__items">
                              <div class="c-grid__item c-grid__item__s__6 c-grid__item__m__8 c-grid__item__l__8">
                                <ValidationProvider :key="`primary_phone_validation`"
                                                    v-slot="{ errors, value, handleChange }"
                                                    v-model="store.attributes.primary_phone.number"
                                                    :name="$t('models.store.phone')"
                                                    rules="tel|min:10|max:11|numeric"
                                                    :vid="`primary_phone_validation`"
                                >
                                  <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                                  <ValidationError :error="errors[0]" />
                                </ValidationProvider>
                              </div>
                            </div>
                          </div>
                          <div class="c-info">
                            <div class="c-info__icon">
                              <Icon name="iconQuestion" />
                            </div>
                            <div class="c-info__content">
                              <p>{{ $t('pages.CompaniesLocationStoreEdit.phone.description') }}</p>
                            </div>
                          </div>
                          <div v-for="(phone, i) in store.attributes.additional_phones" :key="`phone_${i}`">
                            <label class="c-formLabel">
                              <p>{{ formatLabelText({ label: 'models.store.additional_phones', required: false }) }}</p>
                            </label>
                            <div class="c-grid">
                              <div class="phone-form-group c-grid__items">
                                <div class="c-grid__item c-grid__item__s__6 c-grid__item__m__8 c-grid__item__l__8">
                                  <ValidationProvider :key="`phone_${i}_validation`" v-slot="{ errors, value, handleChange }" v-model="phone.number" :name="$t('models.store.phone')" rules="required|tel|min:10|max:11|numeric" :vid="`phone_${i}_validation`">
                                    <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                                    <ValidationError :error="errors[0]" />
                                  </ValidationProvider>
                                </div>
                                <div class="phone-form-remove c-grid__item c-grid__item__s__6 c-grid__item__m__4 c-grid__item__l__4">
                                  <span class="phone-form-remove__button" @click="removePhoneField(i)">
                                    <img :src="iconCrossPath" alt="" class="p-stores__action__icon">
                                    {{ $t('labels.delete') }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="phone-form__add c-button c-button--icon">
                            <div class="c-button__item c-button__item--primary" @click="addPhoneField()">
                              <img :src="ImgIconPlusRed" alt="" class="c-button__icon">
                              <span>{{ $t('labels.add', {'value': $t('models.store.phone')}) }}</span>
                            </div>
                          </div>
                        </fieldset>
                        <fieldset v-if="hasGmbLocation" class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ul class="protection c-checkbox">
                            <li class="c-checkbox__item">
                              <input id="phone_synced" v-model="gmbLocationSynced.phone_synced" type="checkbox" class="c-checkbox__input">
                              <label class="c-checkbox__label" for="phone_synced">
                                {{ $t('pages.CompaniesLocationStore.gmbProtection.edit.protect') }}
                              </label>
                            </li>
                          </ul>
                        </fieldset>
                      </div>
                    </div>
                    <div class="p-formGrid p-formGrid--border">
                      <div class="p-formGrid__items">
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__8 p-formGrid__item__l__8">
                          <label class="c-formLabel">
                            <p>{{ formatLabelText({ label: 'models.store.faxPhoneNumber', required: false }) }}</p>
                          </label>
                          <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="store.attributes.fax_phone_number" :name="$t('models.store.faxPhoneNumber')" rules="tel|min:10|max:11|numeric">
                            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                      </div>
                    </div>
                    <!-- ウェブサイトのリンク -->
                    <div class="p-formGrid p-formGrid--border webSiteLink">
                      <div class="p-formGrid__items">
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__8 p-formGrid__item__l__8">
                          <label class="c-formLabel webSiteLink__title">
                            <p>{{ $t('models.store.web_site_link') }}</p>
                          </label>
                          <KkInfo class="webSiteLink__info">
                            {{ $t('pages.CompaniesLocationStoreEdit.websiteLink.notice') }}
                          </KkInfo>
                          <KkRadioGroup
                            class="webSiteLink__radio"
                            :model-value="websiteLink.isBasicUrlSetting"
                            :items="websiteLink.selectSettingOptions"
                            :name="$t('models.store.web_site_link')"
                            @update:model-value="value => websiteLink.isBasicUrlSetting = value"
                          />
                          <!-- 基本のURL設定の場合 -->
                          <div v-if="websiteLink.isBasicUrlSetting">
                            <label class="c-formLabel">
                              <p>{{ formatLabelText({ label: 'pages.CompaniesLocationStoreEdit.websiteLink.baseUrl', required: false }) }}</p>
                            </label>
                            <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="store.attributes.web_site_url" :name="$t('models.store.web_site_link')" rules="url">
                              <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                              <ValidationError :error="errors[0]" />
                              <KkCheckbox
                                id="store_edit_website_url_synced"
                                class="webSiteLink__protect"
                                :label="$t('pages.CompaniesLocationStoreEdit.websiteLink.protect')"
                                :model-value="gmbLocationSynced.website_url_synced"
                                @update:model-value="value => updateGmbLocationSyncedParam({'syncedKey': 'website_url_synced', value})"
                              />
                            </ValidationProvider>
                          </div>
                          <!-- 連携サイトごとにURLを設定する場合 -->
                          <div v-else>
                            <div v-for="(siteValue, key) in store.attributes.site_types" :key="`web_site_url_${key}`" class="webSiteLink__individualSite">
                              <div v-if="showIndividualInput(siteValue)">
                                <label class="c-formLabel">
                                  <p>{{ $t('pages.CompaniesLocationStoreEdit.websiteLink.individualSite', {siteName: serviceName(key)}) }}{{ $t(`labels.optionalLabel`) }}</p>
                                </label>
                                <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="siteValue.web_site_url" :name="serviceName(key)" rules="url">
                                  <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                                  <ValidationError :error="errors[0]" />
                                  <KkCheckbox
                                    v-if="key === 'google'"
                                    id="store_edit_website_url_synced"
                                    class="webSiteLink__protect"
                                    :label="$t('pages.CompaniesLocationStoreEdit.websiteLink.protect')"
                                    :model-value="gmbLocationSynced.website_url_synced"
                                    @update:model-value="value => updateGmbLocationSyncedParam({'syncedKey': 'website_url_synced', value})"
                                  />
                                </ValidationProvider>
                              </div>
                            </div>
                          </div>
                        </fieldset>
                      </div>
                    </div>
                    <div class="p-formGrid p-formGrid--border">
                      <div class="p-formGrid__items">
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__8 p-formGrid__item__l__8">
                          <label for="" class="c-formLabel">
                            <p>{{ formatLabelText({ label: 'models.store.brand', required: true }) }}</p>
                          </label>
                          <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="store.attributes.brand_id" :name="$t('models.store.brand')" rules="required">
                            <div class="c-select">
                              <select :value="value" @change="handleChange">
                                <option v-for="brand in brands" :key="brand.id" :value="brand.id">
                                  {{ brand.attributes.name }}
                                </option>
                              </select>
                            </div>
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                      </div>
                    </div>
                    <div v-if="showDescription" class="p-formGrid p-formGrid--border">
                      <div class="p-formGrid__items">
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__8 p-formGrid__item__l__8">
                          <label class="c-formLabel">
                            <p>{{ formatLabelText({ label: 'models.store.description', max: 750, currentLength: store.attributes.description.length, required: false }) }}</p>
                          </label>
                          <!-- GMBで<>を含む場合エラーとなるのでexcludedWordでバリデーションしている -->
                          <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="store.attributes.description" name="説明" rules="max:750|excludedWord:<,>">
                            <textarea :value="value" class="c-textArea" col="30" rows="10" @input="handleChange" />
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                        <fieldset v-if="hasGmbLocation" class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ul class="protection c-checkbox">
                            <li class="c-checkbox__item">
                              <input id="description_synced" v-model="gmbLocationSynced.description_synced" type="checkbox" class="c-checkbox__input">
                              <label class="c-checkbox__label" for="description_synced">
                                {{ $t('pages.CompaniesLocationStore.gmbProtection.edit.protect') }}
                              </label>
                            </li>
                          </ul>
                        </fieldset>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <footer v-show="dataLoaded" class="p-box__footer">
            <div class="buttonGroup">
              <KkButton
                color="secondary"
                :text="$t('labels.cancel')"
                class="buttonGroup__item"
                :to="{ name: 'CompaniesLocationStore', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
              />
              <KkButton
                color="primary"
                :text="$t('labels.save')"
                :disabled="!valid"
                class="buttonGroup__item"
                @click="handleSubmit(onSubmit)"
              />
            </div>
          </footer>
        </form>
      </ValidationObserver>
    </section>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import ValidationError from '../components/ValidationError'
import Loading from '../components/Loading.vue'
import iconCrossPath from '../../../images/common/icon_cross.svg'
import ImgIconPlusRed from '../../../images/common/icon_plus_red.svg'
import Icon from '../components/Icon'
import { KkToggleSwitch, KkRadioGroup, KkCheckbox, KkButton } from '../components/base'
import { KkInfo } from '../components/ui'
import StatusBadge from '../components/StatusBadge'
import formatLabelText from '../modules/formatLabelText'
import Page404 from '../views/Page404'
import { castSnakeToCamel } from '../modules/string_converter'

export default {
  name: 'CompaniesStoreEdit',
  components: {
    ValidationError,
    Loading,
    Icon,
    KkToggleSwitch,
    KkRadioGroup,
    KkCheckbox,
    KkInfo,
    KkButton,
    StatusBadge,
    Page404
  },
  emits: ['update-title'],
  data () {
    return {
      prefectures: gon.prefectures,
      companyUuid: this.$route.params.company_uuid,
      storeUuid: this.$route.params.store_uuid,
      enableMultiLang: false,
      iconCrossPath,
      ImgIconPlusRed,
      websiteLink: {
        isBasicUrlSetting: true,
        selectSettingOptions: [
          {
            id: 1,
            value: true,
            text: this.$t('pages.CompaniesLocationStoreEdit.websiteLink.setBaseUrl')
          },
          {
            id: 2,
            value: false,
            text: this.$t('pages.CompaniesLocationStoreEdit.websiteLink.setIndividualUrl')
          },
        ]
      }
    }
  },
  computed: {
    ...mapState({
      store: state => state.companiesLocationStore.data,
      businessTypes: state => state.companiesBusinessTypes.data,
      storeLoading: state => state.companiesLocationStore.loading,
      brands: state => state.companiesBrands.data,
      brandsLoading: state => state.companiesBrands.loading,
      storeGmbLoading: state => state.companiesStoreGmb.loading,
    }),
    ...mapGetters({
      gmbLocationSynced: 'companiesStoreGmb/gmbLocationSynced',
      hasGmbLocation: 'companiesStoreGmb/hasGmbLocation',
      inboundOption: 'companiesCompany/inboundOption',
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
    }),
    loading() {
      return this.storeLoading || this.brandsLoading || this.storeGmbLoading
    },
    dataLoaded() {
      return this.store && this.brands && this.gmbLocationSynced && !this.loading
    },
    showDescription() {
      if (this.businessTypes && this.brands && this.store) {
        const selectedBrand = this.brands.find(brand => brand.id == this.store.attributes.brand_id)
        if (!selectedBrand) return false
        const selectedBusinessTypes = this.businessTypes.data.find(businessType => businessType.id == selectedBrand.attributes.business_type_id)
        if (!selectedBusinessTypes) return false
        return selectedBusinessTypes.attributes.name !== '宿泊'
      } else {
        return false
      }
    },
    existMultiLang() {
      return !!(
        this.store.attributes.store_multi_language.en_name ||
        this.store.attributes.store_multi_language.zh_tw_name ||
        this.store.attributes.store_multi_language.ko_name
      )
    },
    extractStatusNumber() {
      const REGISTERED = 1
      const NOT_ENTERED = 2
      return this.existMultiLang ? REGISTERED : NOT_ENTERED
    }
  },
  async created() {
    this.readBusinessTypes()
    this.readBrands(this.companyUuid)
    await this.readStore({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    this.readStoreGmb({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    this.$emit(
      'update-title',
      this.$t('pages.CompaniesLocationStoreEdit.title', {
        name: this.store.attributes.name
      })
    )
    this.enableMultiLang = this.existMultiLang
    this.setInitialWebSiteSetting()
  },
  methods: {
    ...mapActions({
      readBusinessTypes: 'companiesBusinessTypes/readBusinessTypes',
      readBrands: 'companiesBrands/readBrands',
      readStore: 'companiesLocationStore/readStore',
      updateStore: 'companiesLocationStore/updateStore',
      readStoreGmb: 'companiesStoreGmb/readStoreGmb',
      readStoresRoot: 'companiesStores/readStores',
      updateGmbLocationSyncedParam : 'companiesStoreGmb/updateGmbLocationSyncedParam'
    }),
    formatLabelText,
    showMessage(val) {
      this.$updateFlash({ type: 'notice', message: val })
    },
    addPhoneField() {
      this.store.attributes.additional_phones.push({ id: null, number: '', primary: false })
    },
    removePhoneField(k) {
      this.store.attributes.additional_phones.splice(k, 1)
    },
    addAddressLineField() {
      this.store.attributes.address_lines.push('')
    },
    removeAddressLineField(k) {
      this.store.attributes.address_lines.splice(k, 1)
    },
    // 各サービスのどれか一つでもweb_site_urlに値がある場合、基本のURL設定をfalseにする。
    setInitialWebSiteSetting() {
      const siteTypes = this.store.attributes.site_types
      for (const key in siteTypes) {
        if (siteTypes[key] && siteTypes[key].web_site_url && siteTypes[key].web_site_url.trim() !== '') {
          this.websiteLink.isBasicUrlSetting = false
          return
        }
      }
      this.websiteLink.isBasicUrlSetting = true
    },
    /**
     * 個別の入力フィールドを表示するかどうかを判定
     * @param {Object|null} siteObject - サイト情報。以下の状態を持つ可能性がある。：
     *                                   1. nullである。（サイト連携なし）
     *                                   2. 'web_site_url'キーが存在しない。（そのサイトにサイトリンクを設定する場合がない）
     * @returns {boolean}
     */
    showIndividualInput(siteObject) {
      return siteObject !== null && Object.prototype.hasOwnProperty.call(siteObject, 'web_site_url')
    },
    // 基本のURL設定の場合、サイト連携の設定をnullにする。
    // 連携サイトごとにURLを設定する場合、基本のURL設定をnullにする。
    adjustWebSiteUrl() {
      if (this.websiteLink.isBasicUrlSetting) {
        for (let key in this.store.attributes.site_types) {
          // サイトタイプがオブジェクトで、かつ 'web_site_url' プロパティを持っているかチェック
          if (typeof this.store.attributes.site_types[key] === 'object' && this.store.attributes.site_types[key] !== null) {
            // 'web_site_url' を null に設定
            this.store.attributes.site_types[key]['web_site_url'] = ''
          }
        }
      } else {
        this.store.attributes.web_site_url = ''
      }
    },
    async onSubmit() {
      const result = await this.$refs.form.validate()
      if (!result.valid) {
        alert(this.$t('messages.invalid'))
        return
      }
      this.adjustWebSiteUrl()
      const isSuccess = await this.updateStore(
        {
          company_uuid: this.companyUuid,
          store_uuid: this.storeUuid,
          store: this.store,
          synced: this.hasGmbLocation ? this.gmbLocationSynced : null,
          enableMultiLang: this.enableMultiLang
        }
      )
      if (isSuccess) this.$router.push({ name: 'CompaniesLocationStore', params: { company_uuid: this.companyUuid, store_uuid: this.storeUuid }})
      this.readStoresRoot(this.companyUuid)
    },
    serviceName(service) {
      return this.$t(`labels.sites.${castSnakeToCamel(service)}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.p-formGrid__items {
  display: inline-flex;
  flex-wrap: wrap;
  width: 100%;
  margin: 0 -8px;
  vertical-align: text-top;
  row-gap: 24px;
}

.p-formGrid__item {
  padding: 0 8px;
}

.phone-form {
  &-group {
    margin-bottom: 0;
  }
  &-remove {
    margin-top: 12px;
    &__button {
      cursor: pointer;
      display: inline-flex;
    }
  }
  &__add {
    margin-top: 0;
  }
}

.protection {
  padding-left: 8px;
}

.multiLang {
  background-color: #F4F4F4;
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  &__status {
    font-size: 12px;
  }
  .c-formLabel{
    margin-top: 24px;
  }
}

.webSiteLink {
  &__title {
    margin-bottom: 24px;
  }
  &__info {
    margin-bottom: 24px;
  }
  &__radio {
    margin-bottom: 24px;
  }
  &__protect {
    margin-top: 24px;
  }
  &__individualSite {
    margin-bottom: 24px;
  }
}
.buttonGroup {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  &__item {
    flex-grow: 0;
  }
}
</style>
